import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Check, ArrowRight, Loader2 } from 'lucide-react';
import { API_ROOT } from '@/config';
import SEO from '@/components/seo';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

declare global {
  interface Window {
    turnstile: any;
    onTurnstileLoad: () => void;
  }
}

type AuthMode = 'login' | 'signup' | 'forgot-password';

const AuthMobile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [mode, setMode] = useState<AuthMode>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  // Turnstile
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (token) {
      if (userType === 'brand') {
        navigate('/brand/campaign');
      } else {
        navigate('/upload');
      }
    }
  }, [navigate]);

  // Load Turnstile script on mount (mobile needs to load it itself)
  // Uses the same plain URL as the desktop version (no ?onload or ?render params)
  useEffect(() => {
    if (document.getElementById('turnstile-script')) return;
    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  // Render Turnstile widget using the same polling/retry pattern as desktop.
  // This avoids race conditions with onload callbacks that break on Safari.
  useEffect(() => {
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled) return;
      const turnstile = (window as any).turnstile;
      const container = turnstileRef.current;

      if (!turnstile || !container) {
        // Script not loaded yet or ref not ready — retry
        setTimeout(renderWidget, 300);
        return;
      }

      // Remove old widget if exists
      if (turnstileWidgetId.current) {
        try { turnstile.remove(turnstileWidgetId.current); } catch (e) {}
        turnstileWidgetId.current = null;
      }

      // Render new widget
      turnstileWidgetId.current = turnstile.render(container, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => { if (!cancelled) setTurnstileToken(token); },
        'expired-callback': () => { if (!cancelled) setTurnstileToken(''); },
        theme: 'light',
        size: 'flexible',
      });
    };

    // Delay initial render to ensure DOM is ready (especially inside modals)
    const timer = setTimeout(renderWidget, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (turnstileWidgetId.current) {
        try { (window as any).turnstile?.remove(turnstileWidgetId.current); } catch (e) {}
        turnstileWidgetId.current = null;
      }
    };
  }, [mode]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setSurname('');
    setCompanyName('');
    setError('');
    setSuccess('');
    setTurnstileToken('');
    if (window.turnstile && turnstileWidgetId.current) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Use URLSearchParams (application/x-www-form-urlencoded) to match
      // the backend's OAuth2PasswordRequestForm expectation — same as desktop
      const loginParams = new URLSearchParams();
      loginParams.append('username', email.trim().toLowerCase());
      loginParams.append('password', password);
      loginParams.append('turnstile_token', turnstileToken);

      const response = await fetch(`${API_ROOT}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: loginParams,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({} as any));
        if (response.status === 403) {
          throw new Error('Email not verified. Please check your email and click the verification link.');
        }
        throw new Error(data.detail || 'Login failed. Please check your credentials.');
      }

      const tokenData = await response.json();
      localStorage.setItem('token', tokenData.access_token);
      // Backend /token does not return user_type — always treat as brand (same as desktop)
      localStorage.setItem('userType', 'brand');

      // Always redirect to brand campaign page (same as desktop)
      navigate('/brand/campaign');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!acceptTerms) {
      setError('Please accept the Terms of Service');
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('first_name', firstName.trim());
      formData.append('surname', surname.trim());
      formData.append('email', email.trim().toLowerCase());
      formData.append('password', password);
      formData.append('user_type', 'brand');
      formData.append('turnstile_token', turnstileToken);
      if (companyName.trim()) {
        formData.append('company_name', companyName.trim());
      }
      if (typeof window !== 'undefined') {
        formData.append('frontend_url', window.location.origin);
      }

      const response = await fetch(`${API_ROOT}/register`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Signup failed');
      }

      setSuccess('Account created! Check your email to verify your account. Your 14-day free trial with 30 video checks starts after verification — no credit card required.');
      setTimeout(() => {
        setMode('login');
        resetForm();
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Signup failed. Please try again.');
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!turnstileToken) {
        setError('Please complete the verification challenge.');
        setIsLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('email', email.trim().toLowerCase());
      formData.append('turnstile_token', turnstileToken);
      if (typeof window !== 'undefined') {
        formData.append('frontend_url', window.location.origin);
      }

      const response = await fetch(`${API_ROOT}/forgot-password`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({} as any));
        throw new Error(data.detail || 'Failed to send reset email');
      }

      setSuccess('If an account exists for that email, we\'ve sent a password reset link. Check your inbox and spam folder.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email.');
      if (window.turnstile && turnstileWidgetId.current) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mobile-auth-container">
      <SEO
        title="Sign In"
        description="Sign in or create your BrandComply account. Start launching compliant influencer and UGC campaigns."
        path="/auth"
      />
      {/* Header with Logo */}
      <div className="mobile-auth-header">
        <img 
          src="/brandcomply_logo_full.png" 
          alt="BrandComply" 
          className="mobile-auth-logo"
        />
        <p className="mobile-auth-tagline">
          Launch Influencer & UGC Video Campaigns Faster and On-Brand.
        </p>
      </div>

      {/* Form Section */}
      <div className="mobile-auth-form">
        {/* Tabs */}
        {mode !== 'forgot-password' && (
          <div className="mobile-tabs" style={{ marginBottom: 24 }}>
            <button
              className={`mobile-tab ${mode === 'login' ? 'active' : ''}`}
              onClick={() => { setMode('login'); resetForm(); }}
            >
              Log In
            </button>
            <button
              className={`mobile-tab ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => { setMode('signup'); resetForm(); }}
            >
              Sign Up
            </button>
          </div>
        )}

        {/* Error/Success Messages */}
        {error && (
          <div style={{ 
            background: '#fef2f2', 
            border: '1px solid #fecaca', 
            borderRadius: 12,
            padding: 12,
            marginBottom: 16,
            color: '#dc2626',
            fontSize: 14
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ 
            background: '#f0fdf4', 
            border: '1px solid #bbf7d0', 
            borderRadius: 12,
            padding: 12,
            marginBottom: 16,
            color: '#16a34a',
            fontSize: 14
          }}>
            {success}
          </div>
        )}

        {/* Login Form */}
        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <div className="mobile-input-group">
              <label className="mobile-input-label">Email</label>
              <input
                type="email"
                className="mobile-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="mobile-input-group">
              <label className="mobile-input-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="mobile-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  style={{ paddingRight: 48 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#999',
                    cursor: 'pointer',
                  }}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setMode('forgot-password')}
              style={{
                background: 'none',
                border: 'none',
                color: PALETTE.gunmetal,
                fontSize: 14,
                cursor: 'pointer',
                textDecoration: 'underline',
                marginBottom: 16,
              }}
            >
              Forgot password?
            </button>

            {/* Turnstile Widget */}
            <div ref={turnstileRef} style={{ marginBottom: 16 }} />

            <button
              type="submit"
              className="mobile-btn mobile-btn-primary mobile-btn-full"
              disabled={isLoading || !turnstileToken}
            >
              {isLoading ? (
                <Loader2 size={20} className="mobile-spinner" style={{ width: 20, height: 20 }} />
              ) : (
                <>
                  Log In
                  <ArrowRight size={20} style={{ marginLeft: 8 }} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Signup Form */}
        {mode === 'signup' && (
          <form onSubmit={handleSignup}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="mobile-input-group">
                <label className="mobile-input-label">First Name</label>
                <input
                  type="text"
                  className="mobile-input"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="mobile-input-group">
                <label className="mobile-input-label">Last Name</label>
                <input
                  type="text"
                  className="mobile-input"
                  placeholder="Doe"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="mobile-input-group">
              <label className="mobile-input-label">Company Name</label>
              <input
                type="text"
                className="mobile-input"
                placeholder="Your Company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>

            <div className="mobile-input-group">
              <label className="mobile-input-label">Email</label>
              <input
                type="email"
                className="mobile-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="mobile-input-group">
              <label className="mobile-input-label">Password</label>
              <input
                type="password"
                className="mobile-input"
                placeholder="Min. 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            <div className="mobile-input-group">
              <label className="mobile-input-label">Confirm Password</label>
              <input
                type="password"
                className="mobile-input"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            {/* Terms Checkbox */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 12, 
              marginBottom: 16,
              cursor: 'pointer',
            }}>
              <div
                onClick={() => setAcceptTerms(!acceptTerms)}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  border: `2px solid ${acceptTerms ? PALETTE.gunmetal : '#ccc'}`,
                  background: acceptTerms ? PALETTE.gunmetal : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                {acceptTerms && <Check size={16} color="#fff" />}
              </div>
              <span style={{ fontSize: 14, color: '#666', lineHeight: 1.4 }}>
                I agree to the{' '}
                <a 
                  href="/terms-of-service" 
                  target="_blank"
                  style={{ color: PALETTE.gunmetal, textDecoration: 'underline' }}
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a 
                  href="/privacy-policy" 
                  target="_blank"
                  style={{ color: PALETTE.gunmetal, textDecoration: 'underline' }}
                >
                  Privacy Policy
                </a>
              </span>
            </label>

            {/* Turnstile Widget */}
            <div ref={turnstileRef} style={{ marginBottom: 16 }} />

            <button
              type="submit"
              className="mobile-btn mobile-btn-primary mobile-btn-full"
              disabled={isLoading || !turnstileToken || !acceptTerms}
            >
              {isLoading ? (
                <Loader2 size={20} className="mobile-spinner" style={{ width: 20, height: 20 }} />
              ) : (
                <>
                  Create Account
                  <ArrowRight size={20} style={{ marginLeft: 8 }} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === 'forgot-password' && (
          <form onSubmit={handleForgotPassword}>
            <h2 style={{ 
              fontSize: 24, 
              fontWeight: 600, 
              marginBottom: 8,
              color: PALETTE.gunmetal 
            }}>
              Reset Password
            </h2>
            <p style={{ 
              fontSize: 14, 
              color: '#666', 
              marginBottom: 24 
            }}>
              Enter your email and we'll send you a reset link.
            </p>

            <div className="mobile-input-group">
              <label className="mobile-input-label">Email</label>
              <input
                type="email"
                className="mobile-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            {/* Turnstile Widget */}
            <div ref={turnstileRef} style={{ marginBottom: 16 }} />

            <button
              type="submit"
              className="mobile-btn mobile-btn-primary mobile-btn-full"
              disabled={isLoading || !turnstileToken}
              style={{ marginBottom: 12 }}
            >
              {isLoading ? (
                <Loader2 size={20} className="mobile-spinner" style={{ width: 20, height: 20 }} />
              ) : (
                'Send Reset Link'
              )}
            </button>

            <button
              type="button"
              className="mobile-btn mobile-btn-outline mobile-btn-full"
              onClick={() => { setMode('login'); resetForm(); }}
            >
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthMobile;
